<template>
  <Map
    v-model:area="areaActive"
    v-model:layers="layers"
    v-model:layer="layer"
    v-model:zooms="zooms"
    v-model:zoom="zoom"
    v-model:centroid="report.centroid"
    :areas="areas"
    :class="{ 'fixed-map': true, 'drawing-map': mode == 'draw' }"
    @mouse:up="onMapMouseUp"
  >
    <MapArea
      v-for="(area, index) in areas"
      :key="area.id"
      v-model="areas[index]"
      :selected="areaActive?.id == area.id"
      @click="areaDetailsClicked"
    />

    <MapContactPin
      v-for="(contact, index) in mapPinContacts"
      :key="contact.id || `contact-${index}`"
      :contact="contact"
      :selected="selectedContactId === contact.id"
      @click="selectContact"
    />

    <MapArea v-model="areaDrawing" selected v-if="areaDrawing" />
  </Map>


  <div v-if="report.id == null" class="position-relative d-flex justify-end"  style="height: 40rem;">
    <v-skeleton-loader type="image" width="100%" height="100%" class="position-absolute" />
    <v-skeleton-loader type="list-item-avatar-three-line" class="position-absolute skeleton-progress" />
    <div class="position-absolute skeleton-context d-flex justify-end">
      <v-skeleton-loader type="article" width="20rem" />
      <v-skeleton-loader type="image" width="20rem" class="pa-4" />
    </div>
  </div>

  <div v-else>
    <!-- <v-alert v-if="report.state !== 'completed'" type="info" variant="tonal" class="mb-4">
      Die Suche läuft im Hintergrund weiter. Nach Abschluss kommt eine E-Mail.
    </v-alert> -->

    <div>
      <v-progress-linear v-if="report.state !== 'completed'" color="primary" :indeterminate="report.areas_count == 0" :model-value="(1 / (report.pending_jobs_count + 1)) * 100" height="7" rounded />
    </div>

    <div class="d-flex w-100 justify-space-between mt-3">
      <div class="flex-grow-1 me-3">
        <v-toolbar density="compact" class="translucent-background">
          <template v-slot:prepend>
            <div class="h-100">
              <v-select
                v-model="layer"
                :items="layers"
                item-title="title"
                item-value="identifier"
                class="mt-1"
                density="compact"
              />
            </div>
          </template>

          <div class="d-flex flex-grow-1 ms-4">
            <v-btn density="comfortable" icon="mdi-cursor-move" :active="mode == 'pan'" @click.stop="switchMode('pan')"></v-btn>
            <v-btn density="comfortable" icon="mdi-shape-polygon-plus" :active="mode == 'draw'" @click.stop="switchMode('draw')"></v-btn>
          </div>

          <template v-slot:append v-if="mode == 'draw'">
            <v-btn color="success" density="comfortable" icon="mdi-check" @click.stop="switchMode('pan', true)"></v-btn>
            <v-btn color="error" density="comfortable" icon="mdi-close" @click.stop="switchMode('pan')"></v-btn>
          </template>
        </v-toolbar>

        <v-slider
          v-model="zoom"
          :color="sliderColor"
          icon-color="sliderColor"
          step="1"
          direction="vertical"
          track-size="3"
          prepend-icon="mdi-magnify-minus"
          append-icon="mdi-magnify-plus"
          max-width="2rem"
          max-height="5rem"
          thumb-label="hover"
          :min="minZoom"
          :max="maxZoom"
        />
      </div>

      <Details :report="report" />
    </div>

    <AreaDetails
      v-model="areaActive"
      v-model:selectedContactId="selectedContactId"
      class="translucent-background"
    />

    <Teleport to="#context-navigation-container">
      <AreaList v-model="areaActive" :report="report" :areas="areas" />
    </Teleport>

  </div>
</template>

<script setup>
  import Details from "../components/report/Details.vue";
  import AreaList from "../components/report/AreaList.vue";
  import AreaDetails from "../components/report/AreaDetails.vue";
  import Map from "../components/common/Map.vue";
  import MapArea from "../components/common/MapArea.vue";
  import MapContactPin from "../components/common/MapContactPin.vue";
  import { ref, computed, watch, onMounted } from "vue";
  import { createReportsChannel } from "../services/reports-channel";
  import { createAreasChannel } from "../services/areas-channel";
  import { useCommandStore } from "../stores/command";
  import { useFlashStore } from "../stores/flash";

  const props = defineProps({
    pageProps: {
      type: Object,
      required: true,
    },
    sliderColor: {
      type: String,
      required: false,
      default: "#999"
    }
  });

  const maxZoom = computed(() => {
    return Math.max(...zooms.value.map(z => z.zoom));
  });
  const minZoom = computed(() => {
    return Math.min(...zooms.value.map(z => z.zoom));
  });

  onMounted(() => {
    watch(() => areaActive.value, () => {
      if (areaActive.value) {
        areaDetailsVisible.value = true;
        report.value.centroid = areaActive.value.centroid;
      }
    });
  });

  const command = useCommandStore();
  const flash = useFlashStore();
  const layers = ref([]);
  const layer = ref(null);
  const zooms = ref([]);
  const zoom = ref(null);
  const report = ref({});
  const areas = ref([]);
  const areaActive = ref(null);
  const areaDrawing = ref(null);
  const areaDetailsVisible = ref(false);
  const selectedContactId = ref(null);
  const areasChannel = createAreasChannel(updateArea, {report_id: props.pageProps.report.id});
  const reportsChannel = createReportsChannel(updateReport, {filter: {id: props.pageProps.report.id}});
  const mode = ref("pan")
  const activeAreaContacts = computed(() => areaActive.value?.contacts || []);
  const mapPinContacts = computed(() => {
    return activeAreaContacts.value.filter(contact => {
      return Number.isFinite(Number(contact?.location?.x)) && Number.isFinite(Number(contact?.location?.y));
    });
  });

  function onMapMouseUp(coordinates) {
    if (mode.value != "draw") return;

    if (areaDrawing.value === null) {
      areaDrawing.value = {
        report_id: report.value.id,
        dParts: [ "M" + coordinates.x + "," + coordinates.y ]
      };
    } else {
      areaDrawing.value.dParts[areaDrawing.value.dParts.length - 1] = "L" + coordinates.x + "," + coordinates.y;
    }

    areaDrawing.value.dParts.push("Z");
    areaDrawing.value.svg_paths = {paths: [areaDrawing.value.dParts.join(" ")]};
  }

  function updateArea(newArea) {
    const index = areas.value.findIndex(a => a.id === newArea.id);

    if (newArea.destroyed) {
      if (index !== -1) {
        areas.value.splice(index, 1);
      }
      return;
    }

    if (index !== -1) {
      areas.value[index] = newArea;
      if (areaActive.value?.id === newArea.id) {
        areaActive.value = areas.value[index];
      }
    } else {
      areas.value.push(newArea);
    }
  }

  function updateReport(newReport) {
    const oldCentroid = report.value?.centroid;
    Object.assign(report.value, newReport);
    if (oldCentroid) {
      report.value.centroid = oldCentroid;
    }
  }

  function areaDetailsClicked(area) {
    areaActive.value = area;
    areaDetailsVisible.value = true;
    selectedContactId.value = null;
  }

  function selectContact(contact) {
    selectedContactId.value = contact?.id || null;
  }

  async function switchMode(newMode, commit = false) {
    const oldMode = mode.value;

    switch(oldMode) {
      case "draw":
        mode.value = await exitDrawMode(commit);
        break;

      case "pan":
        mode.value = newMode;
        break;
    }
  }

  async function exitDrawMode(commit, newMode = "pan") {
    if (areaDrawing.value && commit) {
      areaDrawing.value.svg_paths.paths[0] += " Z";
      const result = await command.execute("Reporting::Area", "create", {reporting_area: areaDrawing.value});
      if (result.success) {
        await enqueueAnalysisJobs(result.data.id);
      } else {
        result.errors.forEach(flash.error);
        return "draw";
      }
    }
    areaDrawing.value = null;
    return newMode;
  }

  async function enqueueAnalysisJobs(areaId) {
    const data = await command.execute("Reporting::Area", "enqueue_analysis_jobs", {id: areaId});
    if (!data.success) {
      data.errors.forEach(flash.error);
    }
  }
</script>

<style scoped>
  .fixed-map {
    position: absolute;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
  }

  .translucent-background {
    background-color: rgba(255, 255, 255, 0.8);
  }

  ::v-deep .v-skeleton-loader > * {
    height: 100%;
  }

  .skeleton-progress {
    top: 0;
    right: 0;
    width: 15rem;
    height: 8rem;
    margin:15px;
  }

  .skeleton-context {
    bottom: 0;
    right: 0;
    width: 100%;
    height: 10rem;
    margin:15px;
  }

  .drawing-map {
    cursor: crosshair;
  }
</style>
